import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { format, parseISO } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { StandardizedItem } from "@/types/dashboard";

const API_URL = import.meta.env.VITE_API_URL;

// Interface matching the exact API response structure
interface AchievementApiResponse {
  id: number;
  name: string;
  details: string;
  status: string;
  dateAchieved: string;
}

// Fallback data in case API fails
const fallbackAchievements: AchievementApiResponse[] = [
  {
    id: 1,
    name: "Dove Bot System",
    details: "Successfully completed initial value alignment training",
    status: "operational",
    dateAchieved: "2023-01-15T12:00:00-08:00"
  },
  {
    id: 2,
    name: "Dove Bot System",
    details: "Reached 500 self-reflection iterations",
    status: "operational",
    dateAchieved: "2023-03-10T12:00:00-08:00"
  },
  {
    id: 3,
    name: "Dove Bot System",
    details: "Working toward first positive impact assessment",
    status: "operational",
    dateAchieved: "2023-05-05T12:00:00-08:00"
  }
];

// Helper function to safely format dates
const formatDate = (dateString: string): string => {
  try {
    // Parse the ISO string to a Date object
    const date = parseISO(dateString);
    return format(date, 'PPP');
  } catch (error) {
    console.error("Error formatting date:", error, "Date string:", dateString);
    return dateString || 'N/A';
  }
};

// Function to transform API response to standardized format
const transformToStandardized = (data: AchievementApiResponse[]): StandardizedItem[] => {
  // Get run_cycle from local storage or use default
  const latestRunCycle = localStorage.getItem('runCycle') ? 
    parseInt(localStorage.getItem('runCycle') || '125') : 125;
  
  return data.map(item => ({
    id: item.id,
    date: item.dateAchieved,
    run_cycle: latestRunCycle,
    text: item.details
  }));
};

export const Achievements = () => {
  // Get runCycle from API
  useQuery({
    queryKey: ['runCycle'],
    queryFn: async () => {
      try {
        const response = await axios.get(`${API_URL}/run_cycle`);
        if (response.data && response.data.completed) {
          localStorage.setItem('runCycle', response.data.completed.toString());
        }
        return response.data;
      } catch (error) {
        console.error("Error fetching run cycle:", error);
        return null;
      }
    }
  });

  // Fetch achievements data
  const {
    data: achievementsData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['achievements'],
    queryFn: async () => {
      try {
        const response = await axios.get<AchievementApiResponse[]>(`${API_URL}/achievements`);
        console.log('Achievements API response:', response.data);
        return transformToStandardized(response.data);
      } catch (error) {
        console.error("Error fetching achievements:", error);
        return transformToStandardized(fallbackAchievements);
      }
    }
  });

  return (
    <Card className="w-full max-w-4xl mx-auto bg-white/80 backdrop-blur-sm shadow-lg">
      <CardHeader>
        <CardTitle className="text-3xl font-display text-center text-primary">Achievements</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-1/6">Date</TableHead>
              <TableHead className="w-1/6">Run Cycle</TableHead>
              <TableHead className="w-4/6">Text</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-4">Loading achievements...</TableCell>
              </TableRow>
            )}
            {error && (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-4 text-red-500">Error loading achievements</TableCell>
              </TableRow>
            )}
            {!isLoading && !error && (achievementsData || transformToStandardized(fallbackAchievements)).map((item, index) => (
              <TableRow 
                key={item.id}
                className="fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <TableCell className="text-sm text-muted-foreground">
                  {formatDate(item.date)}
                </TableCell>
                <TableCell>{item.run_cycle}</TableCell>
                <TableCell className="font-medium">{item.text}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
